import { Error, SearchLocate } from "@carbon/icons-react";
import { t } from "i18next";
import { useState } from "react";
import { useParams } from "react-router-dom";

import PageLoader from "../../../../components/PageLoader";
import AsyncButton from "../../../../components/buttons/AsyncButton";
import CancelButton from "../../../../components/buttons/backButton/CancelButton";
import FallbackComponent from "../../../../components/fallbackComponent/FallbackComponent";
import useSplitInboundDeliveryMutation from "../../../../hooks/graphql-hooks/mutations/useSplitInboundDeliveryMutation";
import useInboundDeliveryItemsByIdQuery from "../../../../hooks/graphql-hooks/queries/useInboundDeliveryItemsByIdQuery";
import { IDeliveryItem } from "../../../../models/deliveryItem";
import { isNullOrEmpty } from "../../../../utils/helper";
import {
  mapToIDeliveryItems,
  mapToSplitInboundDeliveryInput,
} from "../../../../utils/mapper";
import TransferableList from "../transferableList/TransferableList";

interface TransferItemsProps {
  setNewSplitDeliveryId: (id: string) => void;
  setHeaderItemsAmount: (amount: number) => void;
}

const TransferItems = (props: TransferItemsProps) => {
  const { id: idFromUrl } = useParams();
  const { setNewSplitDeliveryId, setHeaderItemsAmount } = props;

  const [originalItems, setOriginalItems] = useState<IDeliveryItem[]>([]);
  const [newItems, setNewItems] = useState<IDeliveryItem[]>([]);

  const { loading: loadingQuery, error } = useInboundDeliveryItemsByIdQuery({
    id: idFromUrl,
    dataAction: (itemsFragment) => {
      setOriginalItems(mapToIDeliveryItems(itemsFragment.items));
      setHeaderItemsAmount(itemsFragment.items.length);
    },
    errorAction: () => {},
  });

  const [splitMutation, { loading: loadingMutation }] =
    useSplitInboundDeliveryMutation({
      dataAction: (output) => {
        setNewSplitDeliveryId(output.splitDelivery.id);
        setHeaderItemsAmount(newItems.length);
      },
    });

  const sendSplitMutation = () => {
    if (!idFromUrl || newItems.length === 0) return;

    const splitInboundDeliveryInput = mapToSplitInboundDeliveryInput(
      idFromUrl,
      newItems,
    );

    if (!splitInboundDeliveryInput) return;

    splitMutation({ variables: { splitInboundDeliveryInput } }).catch(() => {});
  };

  const renderTransferList = () => {
    if (isNullOrEmpty(idFromUrl))
      return (
        <FallbackComponent middleIcon={<SearchLocate />} text={t("idEmpty")} />
      );

    if (loadingQuery) return <PageLoader />;

    if (error && originalItems.length === 0) {
      return (
        <FallbackComponent
          middleIcon={<Error size={32} />}
          text={t("deliveryItemsError")}
        />
      );
    }

    return (
      <TransferableList
        originalItems={originalItems}
        newItems={newItems}
        disableTransferButtons={loadingMutation}
        setOriginalItems={setOriginalItems}
        setNewItems={setNewItems}
      />
    );
  };

  return (
    <div className="split-container mt-20" data-testid="splitOrder">
      {renderTransferList()}
      <div>
        <div className="page-content-button-container-button-full-width">
          <AsyncButton
            sendRequest={sendSplitMutation}
            loading={loadingMutation}
            disabled={newItems.length === 0 || loadingMutation}
            variant="contained"
          >
            {t("createSplitOrder")}
          </AsyncButton>
        </div>
        <div className="page-content-button-container">
          <div className="page-content-button-container-button-center">
            <CancelButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferItems;
